import { Pagination } from "reusableComponents";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { useLanguage } from "context/languageContext";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getOtherFilterOptions } from "services/filters.services";
import { getFiltersForCustomPage } from "services/customPage.services";
import { getSpacesByFilters } from "services/spaceList.services";
import { setFilters } from "store/filters/filtersSlice";
import { Filters, SpacesList } from "views/SpacesListPage/components";

import styles from "./styles.module.scss";

// Helper function to get correct base URL based on current domain
const getBaseUrl = () => {
  if (typeof window === 'undefined') return 'https://chooseandwork.com';
  
  const hostname = window.location.hostname;
  const cleanHost = hostname.replace(/^www\./, '');
  
  if (cleanHost.includes('chooseandwork.be')) return 'https://chooseandwork.be';
  if (cleanHost.includes('chooseandwork.fr')) return 'https://chooseandwork.fr';
  return 'https://chooseandwork.com';
};

// Helper to get language locale
const getLocale = (language) => {
  switch(language) {
    case 'fr': return 'fr_FR';
    case 'es': return 'es_ES';
    default: return 'en_US';
  }
};

const CustomPage = () => {
  const filters = useSelector((state) => state.filters);
  const navigate = useNavigate();
  const { customPageID } = useParams();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [moreFilterOptions, setMoreFilterOptions] = useState({});
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 16,
    current_page: 1,
    next_page_url: null,
    prev_page_url: null,
    has_more_pages: true,
  });

  const [activeSpace, setActiveSpace] = useState(null);
  const [filterHistory, setFilterHistory] = useState([filters]);

  // Keep track of the initial custom page filters returned by backend so we can reset page when user changes filters
  const customPageFiltersRef = useRef(filters);

  const [subtitle, setSubtitle] = useState();
  const [text, setText] = useState();
  const [meta, setMeta] = useState(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [pageTitle, setPageTitle] = useState();

  const { t } = useTranslation("global");

  // Maintain filter history (max depth 7)
  useEffect(() => {
    const lastFilters = filterHistory[filterHistory.length - 1];
    if (JSON.stringify(filters) !== JSON.stringify(lastFilters)) {
      setFilterHistory((prevHistory) => {
        const newHistory = [...prevHistory, filters];
        if (newHistory.length > 7) newHistory.shift();
        return newHistory;
      });
    }
  }, [filters, filterHistory]);

  const handleResetFilters = () => {
    if (filterHistory.length > 1) {
      const previousFilters = filterHistory[filterHistory.length - 2];
      dispatch(setFilters(previousFilters));
      setFilterHistory((prevHistory) => prevHistory.slice(0, -1));
    }
  };

  const handleSelectSpace = (space) => setActiveSpace(space);
  const handleDeselectSpace = () => setActiveSpace(null);

  // Fetch initial custom page data (filters, subtitle, text, meta) on mount or when customPageID / language changes
  useEffect(() => {
    let cancelled = false;

    const fetchFilters = async () => {
      setLoading(true);
      setError(null);
      setSubtitle(null);
      setText(null);
      setMeta(null);
      setPageTitle(null);

      try {
        const response = await getFiltersForCustomPage(customPageID, language);
        
        if (cancelled) return;

        customPageFiltersRef.current = response.filters || {};
        dispatch(setFilters(response.filters || {}));
        setSubtitle(response.subtitle || null);
        setText(response.text || null);
        setPageTitle(response.title || null);
        setMeta(response.meta || null);

        // reset pagination to page 1 on new custom page load
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        setFiltersInitialized(true);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load page data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFilters();

    return () => {
      cancelled = true;
    };
  }, [customPageID, language, dispatch]);

  useEffect(() => {
    if (!filtersInitialized) return;

    let cancelled = false;

    const fetchSpaces = async () => {
      setActiveSpace(null);
      setLoading(true);
      setSpaces([]);
      setError(null);

      try {
        // if user changed filters compared to initial custom page filters, reset to page 1
        const page =
          JSON.stringify(filters) !== JSON.stringify(customPageFiltersRef.current)
            ? 1
            : pagination.current_page;

        // Create a copy of filters and exclude the "city" property
        const { city, ...filtersWithoutCity } = filters;

        const response = await getSpacesByFilters({
          ...filtersWithoutCity,
          page,
          perPage: pagination.per_page,
          lang: language,
        });

        if (cancelled) return;

        setSpaces(response.data || []);
        setPagination((prevState) => ({
          ...prevState,
          total: response.pagination?.total || 0,
          per_page: response.pagination?.per_page || pagination.per_page,
          current_page: response.pagination?.current_page || page,
          has_more_pages: response.pagination?.has_more_pages ?? false,
          next_page_url: response.pagination?.next_page_url ?? null,
          prev_page_url: response.pagination?.prev_page_url ?? null,
        }));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to fetch spaces. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const fetchFilterOptions = async () => {
      try {
        // Also exclude city from filter options request if needed
        const { city, ...filtersWithoutCity } = filters;
        
        const options = await getOtherFilterOptions({
          ...filtersWithoutCity,
          lang: language,
        });
        if (!cancelled) setMoreFilterOptions(options || {});
      } catch (err) {
        // Silently fail for filter options, not critical
      }
    };

    // Kick off requests
    fetchSpaces();
    fetchFilterOptions();

    // Update refs
    customPageFiltersRef.current = filters;

    // Scroll to top after new results
    const scrollTimeout = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(scrollTimeout);
    };
  }, [filters, pagination.current_page, filtersInitialized, language]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage !== pagination.current_page) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: newPage,
      }));
    }
  };

  // Redirect on error via effect
  useEffect(() => {
    if (error) {
      navigate("/notfound", { replace: true });
    }
  }, [error, navigate]);

  // Get dynamic base URL based on current domain
  const baseUrl = getBaseUrl();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  
  // Determine SEO values - USE META OBJECT FIRST
  const seoTitle = meta?.title || pageTitle || subtitle || t("search.title") || "Choose&Work";
  const seoDescription = meta?.description || "Meeting rooms, private offices, coworking spaces and virtual offices. Find your perfect workspace with Choose&Work.";
  const seoKeywords = meta?.keywords || "coworking, meeting rooms, private offices, virtual offices, workspace";
  const seoRobots = meta?.robots || "";
  
  // Build canonical URL - use meta.canonical if provided, otherwise use current domain + path
  let canonicalUrl = meta?.canonical;
  if (!canonicalUrl) {
    canonicalUrl = `${baseUrl}${currentPath}`;
  }
  
  // Determine if this should be indexed
  const shouldIndex = !seoRobots?.includes('noindex') && pagination.current_page <= 1;
  
  // Build alternate URLs for hreflang
  const getAlternateUrls = () => {
    const alternates = [];
    const domains = {
      'en': 'https://chooseandwork.com',
      'fr': 'https://chooseandwork.fr',
      'be': 'https://chooseandwork.be'
    };
    
    // Add English version
    alternates.push({
      lang: 'en',
      url: `https://chooseandwork.com${currentPath}`
    });
    
    // Add French (France) version
    alternates.push({
      lang: 'fr-fr',
      url: `https://chooseandwork.fr${currentPath}`
    });
    
    // Add French (Belgium) version
    alternates.push({
      lang: 'fr-be',
      url: `https://chooseandwork.be/fr${currentPath}`
    });
    
    return alternates;
  };

  const alternateUrls = getAlternateUrls();

  return (
    <>
      {/* SEO: use backend meta when available */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        
        {/* Dynamic robots based on pagination and filters */}
        {!seoRobots && pagination.current_page > 1 && (
          <meta name="robots" content="noindex, follow" />
        )}
        {!seoRobots && JSON.stringify(filters) !== JSON.stringify(customPageFiltersRef.current) && (
          <meta name="robots" content="noindex, follow" />
        )}
        {seoRobots && <meta name="robots" content={seoRobots} />}
        
        {/* Canonical URL - now dynamic based on domain */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* HREFLANG tags for international SEO */}
        {alternateUrls.map((alt) => (
          <link 
            key={alt.lang} 
            rel="alternate" 
            hrefLang={alt.lang} 
            href={alt.url} 
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`https://chooseandwork.com${currentPath}`} />
        
        {/* Open Graph Tags - use dynamic canonical URL */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={getLocale(language)} />
        <meta property="og:site_name" content="Choose&Work" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:site" content="@chooseandwork" />
        
        {/* Additional meta tags for better SEO */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      </Helmet>

      <section className={styles.customPage}>
        <Filters
          moreFilterOptions={moreFilterOptions}
          spaces={spaces}
          activeSpace={activeSpace}
          setActiveSpace={handleSelectSpace}
        />
        <div className={`${styles.bodyWrapper} pageContainer`}>
          <div className={styles.pageTitle}>
            <a href="/">{t("home.title")}</a> /<span>{t("search.title")}</span>
          </div>

          {subtitle && (
            <div className={styles.customPageSubtitle}>
              <div className={styles.subtitleContent}>
                {/* Use H1 for main heading - important for SEO */}
                <h1>{subtitle}</h1>
              </div>
              <span className={styles.totalAmount}>
                {t("search.total")}
                {pagination.total}
              </span>
            </div>
          )}

          <SpacesList
            loading={loading}
            error={error}
            spaces={spaces}
            pagination={pagination}
            onHoverSpace={handleSelectSpace}
            onLeaveSpace={handleDeselectSpace}
            resetPrevFilters={handleResetFilters}
            showResetButton={filterHistory.length > 2}
          />

          {!loading && !error && pagination.total > 0 && (
            <Pagination
              itemsPerPage={pagination.per_page}
              totalItems={pagination.total}
              currentPage={pagination.current_page}
              onPageChange={handlePageChange}
              hasMorePages={pagination.has_more_pages}
            />
          )}

          {text && (
            <div
              className={styles.customPageText}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default CustomPage;